import { type ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetHeaderCloseButton,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import styles from './ScopeChangeRequestRejectDialog.module.css'
import { byDesign } from '@/infra/design'
import imgMissingPermissionsUrl from '@/assets/misc/img_missing-permissions.svg'
import JobNoLongerAvailableIllustration from '@/components/SpotIllustration/JobNoLongerAvailableIllustration'
import { type ScopeChangeRequestRejectVariant } from './ScopeChangeRequestRejectDialogViewModel'

const copy = {
  Decline: {
    title: 'Do you want to decline this change request?',
    rejectedAs: 'declined',
    submitLabel: 'Decline',
    submitTestId: 'scope-change-request-reject',
  },
  RejectOnBehalfOfClient: {
    title: 'Are you sure that you want to reject on behalf of the client?',
    rejectedAs: 'rejected',
    submitLabel: 'Reject',
    submitTestId: 'scope-change-request-reject-client',
  },
} satisfies Record<ScopeChangeRequestRejectVariant, unknown>

export interface ScopeChangeRequestRejectDialogProps {
  variant: ScopeChangeRequestRejectVariant
  showing: boolean
  onClose: () => unknown
  onReject: () => unknown
  isRejectPending: boolean
  corporateMode: boolean
  /**
   * The reason text area.
   */
  reasonField: ReactNode
}

export function ScopeChangeRequestRejectDialog({
  variant,
  showing,
  onClose,
  onReject,
  isRejectPending,
  corporateMode,
  reasonField,
}: ScopeChangeRequestRejectDialogProps) {
  const { title, rejectedAs, submitLabel, submitTestId } = copy[variant]
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isRejectPending ? undefined : onClose}
    >
      <ModalSheetHeader>
        <div className={styles.closeButtonContainer}>
          <ModalSheetHeaderCloseButton testId="scope-change-request-close-reject-dialog" />
        </div>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          {!corporateMode && (
            <ModalSheetContentIllustration
              src={imgMissingPermissionsUrl}
              illustration={byDesign(
                undefined,
                JobNoLongerAvailableIllustration,
              )}
              alt="Reject"
            />
          )}
          <ModalSheetContentTitle>{title}</ModalSheetContentTitle>
          <ModalSheetContentDescription>
            You will need to provide a clear reason as to why the requested
            changes are {rejectedAs}.
          </ModalSheetContentDescription>
          <div className={styles.reasonContainer}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- reasonField is an injected node; its input id is not known here */}
            <label className={styles.reasonLabel}>Reason</label>
            {reasonField}
          </div>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            isDisabled={isRejectPending}
            onPress={onClose}
          />
          <Button
            label={submitLabel}
            onPress={onReject}
            variant="destructive"
            testId={submitTestId}
            isPending={isRejectPending}
            isDisabled={isRejectPending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
